import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ServiceCard from "src/sections/pages/services/service-card";
import { useTranslation } from "react-i18next";
import axios from "../../../../axios";
import SideBar from "../../SideBar";
import Notification from "../components/Notification";
import PreviewOverlay from "../components/PreviewOverlay";
import Title from "../components/Title";
import LangInputs from "../activities/LangInputs";
import FeaturesInput from "./FeaturesInput";
import PriceOptionsInput from "./PriceOptionsInput";

// Все языки сайта. Копируем объект целиком, чтобы не потерять язык,
// которого нет в этом списке.
const LANGS = ["en", "ru", "kk"];

const langObject = (source) => {
  const result = { ...(source || {}) };
  for (const lang of LANGS) if (!result[lang]) result[lang] = "";
  return result;
};

// Shared form for creating and editing a service.
// mode: "create" | "edit". For "edit", pass `initial` (the service object with _id).
export default function ServiceForm({ mode = "create", initial = null }) {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isEdit = mode === "edit";

  const [imageUrl, setImageUrl] = useState(initial?.imageUrl || "");
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [show, setShow] = useState(false);
  const [text, setText] = useState("");
  const [subText, setSubText] = useState("");
  const [state, setState] = useState("success");
  const inputFileRef = useRef(null);

  const [formData, setFormData] = useState({
    name: langObject(initial?.name),
    description: langObject(initial?.description),
    price: initial?.price || "",
    priceText: langObject(initial?.priceText),
    isTranslator: Boolean(initial?.isTranslator),
    features: initial?.features || [],
    priceOptions: initial?.priceOptions || [],
    priceNote: initial?.priceNote || {},
    requestFields: initial?.requestFields || {},
    formType: initial?.formType || "basic",
  });

  const notify = (t, st, s = "") => {
    setText(t);
    setSubText(s);
    setState(st);
    setShow(true);
    setTimeout(() => setShow(false), 3500);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "price") {
      setFormData((prev) => ({ ...prev, price: value }));
    } else {
      const [field, lang] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [field]: { ...prev[field], [lang]: value },
      }));
    }
  };

  const handleChangeFile = async (e) => {
    try {
      const fd = new FormData();
      fd.append("image", e.target.files[0]);
      const { data } = await axios.post("/upload", fd);
      setImageUrl(data.url);
    } catch (error) {
      notify("Не удалось загрузить файл!", "warn", "Попробуйте файл другого типа!");
      console.warn(error);
    }
  };

  const buildPayload = () => ({ ...formData, imageUrl });

  // Перед сохранением показываем предпросмотр — сохраняем только после «ок».
  const handlePreviewClick = () => {
    if (
      !formData.name.en ||
      !formData.name.ru ||
      !formData.description.en ||
      !formData.description.ru ||
      !formData.price
    ) {
      notify("Пустые данные!", "warn", "Пожалуйста заполните все поля.");
      return;
    }
    setPreviewing(true);
  };

  const handleSubmit = async () => {
    const payload = buildPayload();
    setSaving(true);
    try {
      if (isEdit) {
        await axios.patch(`/services/${initial._id}`, payload);
      } else {
        await axios.post("/services", payload);
      }
      notify(
        isEdit ? "Услуга обновлена!" : "Услуга создана!",
        "success",
        "Перенаправляем вас к списку услуг."
      );
      setTimeout(() => navigate("/admin/services"), 1500);
    } catch (error) {
      setPreviewing(false);
      notify("Ошибка сохранения", "error", "Попробуйте ещё раз.");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "mt-1 w-full rounded-md border-0 py-1.5 px-4 text-neutral-900 ring-1 ring-inset ring-neutral-300 placeholder:text-neutral-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6";
  const labelCls = "block text-sm font-medium text-neutral-700";

  return (
    <>
      {previewing && (
        <PreviewOverlay
          title="Так услугу увидит клиент"
          confirmLabel={isEdit ? "Всё верно — сохранить" : "Всё верно — создать"}
          saving={saving}
          onBack={() => setPreviewing(false)}
          onConfirm={handleSubmit}
        >
          <div className="max-w-sm">
            <ServiceCard service={buildPayload()} lang={i18n.language} />
          </div>
        </PreviewOverlay>
      )}
    <SideBar>
      <Notification
        show={show}
        setShow={setShow}
        text={text}
        subText={subText}
        state={state}
      />
      <Title text={isEdit ? "Редактировать услугу" : "Создать услугу"} />
      <div className="w-full flex justify-start items-center">
        <div className="p-4 rounded-md max-w-3xl w-full">
          <div className="w-full flex flex-col gap-y-4 md:px-4">
            <LangInputs
              label="Название"
              field="name"
              values={formData.name}
              onChange={handleInputChange}
              placeholders={{ en: "Tour Guide", ru: "Гид", kk: "Гид" }}
            />
            <LangInputs
              label="Описание"
              field="description"
              values={formData.description}
              onChange={handleInputChange}
              textarea
              placeholders={{
                en: "A professional local guide...",
                ru: "Профессиональный местный гид...",
                kk: "Кәсіби жергілікті гид...",
              }}
            />

            <div className="w-full max-w-sm">
              <label htmlFor="price" className={labelCls}>
                Цена числом
              </label>
              <input
                id="price"
                name="price"
                type="text"
                className={inputCls}
                placeholder="50 000 ₸"
                value={formData.price}
                onChange={handleInputChange}
              />
              <p className="mt-1 text-xs text-neutral-500">
                Сумма одинакова на всех языках, переводить её не нужно.
              </p>
            </div>

            {/* Цена словами переводится, сумма — нет. Пока этого поля не было,
                на английской и японской версии в строке цены висело русское
                «Договорная». Заполнено — показывается вместо числа. */}
            <LangInputs
              label="Цена словами (если суммы нет)"
              field="priceText"
              values={formData.priceText}
              onChange={handleInputChange}
              placeholders={{
                ru: "Договорная",
                en: "Negotiable",
                kk: "Келісімді",
              }}
            />

            <FeaturesInput
              value={formData.features}
              onChange={(features) =>
                setFormData((prev) => ({ ...prev, features }))
              }
            />

            <PriceOptionsInput
              value={formData.priceOptions}
              onChange={(priceOptions) =>
                setFormData((prev) => ({ ...prev, priceOptions }))
              }
              note={formData.priceNote}
              onNoteChange={(priceNote) =>
                setFormData((prev) => ({ ...prev, priceNote }))
              }
            />

            {/* АНКЕТА ПЕРЕД WHATSAPP.
                Кнопка услуги открывает короткую анкету, ответы уходят в чат
                вместе с названием услуги. Обычной хватает имени и числа
                человек; трансферу нужны точки подачи, багаж и время. */}
            <label className="block max-w-md">
              <span className={labelCls}>Анкета перед WhatsApp</span>
              <select
                className={inputCls}
                value={formData.formType}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, formType: e.target.value }))
                }
              >
                <option value="basic">Обычная — имя и количество человек</option>
                <option value="transfer">
                  Трансфер — откуда, куда, багаж, дата и время
                </option>
              </select>
            </label>

            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={formData.isTranslator}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    isTranslator: e.target.checked,
                  }))
                }
                className="mt-1 h-4 w-4 rounded border-neutral-300 text-sky-600 focus:ring-sky-500"
              />
              <span>
                <span className="block text-sm font-medium text-neutral-900">
                  Это услуга переводчика
                </span>
                <span className="block text-sm text-neutral-500">
                  Цену этой услуги увидит клиент в форме заявки, когда поставит
                  галочку «нужен переводчик». Отмечайте только одну услугу.
                </span>
              </span>
            </label>

            <button
              className="self-start inline-flex items-center px-2 py-1 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-sky-600 hover:bg-sky-500"
              onClick={() => inputFileRef.current.click()}
            >
              Загрузить фото
            </button>
            <input
              ref={inputFileRef}
              type="file"
              onChange={handleChangeFile}
              hidden
            />
            {imageUrl && (
              <>
                <button
                  className="self-start inline-flex items-center px-2 py-1 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-500"
                  onClick={() => setImageUrl("")}
                >
                  Удалить файл
                </button>
                <img
                  src={`${process.env.REACT_APP_BASE_API}${imageUrl}`}
                  alt=""
                  className="max-w-xs rounded-md"
                />
              </>
            )}

            <div className="mt-4 w-full flex justify-end items-center gap-x-4">
              <button
                type="button"
                onClick={() => navigate("/admin/services")}
                className="inline-flex items-center px-4 py-2 border border-neutral-300 text-base font-medium rounded-md shadow-sm text-neutral-900 hover:bg-neutral-300"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={handlePreviewClick}
                className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-sky-600 hover:bg-sky-500"
              >
                {isEdit ? "Предпросмотр и сохранить" : "Предпросмотр и создать"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </SideBar>
    </>
  );
}
